import React, { useEffect, useMemo, useRef, useState } from "react";
import { DeploymentResponseDto } from "../common/ObjectTypes";
import { useProfile } from "../common/ProfileProvider";
import { getDeployments } from "../../api/deploymentsApi";
import DeploymentsList from "../deployments/DeploymentsList";
import SearchBarDeployments from "../deployments/SearchBarDeployments";
import { DeploymentSearchQueryType } from "../common/QueryTypes";
import { pageSize } from "../../api/apiUtils";
import { useLocation } from "react-router-dom";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import tailwindClassnames from "../common/TailwindClassnames";
import ImportFromCsv from "../csvImport/ImportFromCsv";
import FollowImportUntilSettled from "../csvImport/FollowImportUntilSettled";
import {
  appliedTemplate,
  entityCopy,
  schemaColumns,
} from "../csvImport/csvImport";
import { getCsvImport } from "../../api/migrationsApi";
import type { CsvImportJobDto } from "../../api/migrationsApi";
import { canOpenWildlifeInsightsMigration } from "./migrationEntry";

type ManageProjectDeploymentsProps = {
  projectId: number;
  projectGuid: string;
  /**
   * The organisation the project belongs to (AKL-815). `ImportFromCsv` sends it as `org_id`
   * alongside `target_project_id`. `ManageProjectDetails` derives it — and `projectId` — with
   * `Number(...)` from state its project load fills, and still mounts this tab when that load
   * fails, so either can be NaN; the import entry points are withheld unless both are real
   * numbers.
   */
  orgId: number;
  perms: any[];
  /**
   * The project's deployment template (`deployment_schema`) and its `ui_schema`, for the
   * columns the Akili CSV format reads beyond the fixed six (AKL-815, `schemaColumns`).
   * `ManageProjectDetails` passes its `originalProject`, which is loaded before any tab
   * renders, refreshed after every save and re-read when an import applies a template
   * (`onTemplateApplied`), so no columns here means the template adds none a cell can hold
   * (or there is no template) — never "not loaded yet".
   */
  deploymentSchema?: unknown;
  uiSchema?: unknown;
  /**
   * Called once when an applied import's report says it applied a deployment template to
   * the project (the Wildlife Insights format does that to a project without one), so the
   * owner of the project state can re-read it — `deploymentSchema`/`uiSchema` above are
   * stale from that moment, and the next import dialog would list no template columns.
   * Fires however the import reached `applied`: seen by this tab's dialog, let go of by it
   * while still applying, or handed over by the Add Deployment page.
   */
  onTemplateApplied?: () => void;
};

/** The router state the Add Deployment page hands over when it leaves for this tab (AKL-815). */
type DeploymentsTabState = { followImportGuids?: unknown } | null | undefined;

const ManageProjectDeployments: React.FC<ManageProjectDeploymentsProps> = ({
  projectId,
  projectGuid,
  orgId,
  perms,
  deploymentSchema,
  uiSchema,
  onTemplateApplied,
}) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { profile, profileLoading } = useProfile();
  const [deployments, setDeployments] = useState<DeploymentResponseDto[]>([]);
  const [listLoading, setListLoading] = useState(true);
  const [canAddDeployment, setCanAddDeployment] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  const [importOpen, setImportOpen] = useState(false);
  // Imports the dialog let go of while the server was still applying them, plus the ones
  // the Add Deployment page handed over through router state. The dialog's own poller stops
  // with the dialog, so the tab follows each one until it settles and then refreshes the
  // list — otherwise an apply that finishes after the close leaves the list stale. A list,
  // not a single slot: letting go of a second job must not stop following the first.
  const [pendingImportGuids, setPendingImportGuids] = useState<string[]>([]);
  // The guids that arrived through `location.state.followImportGuids`. Only these get a
  // toast when they settle: the user left the Add page before seeing the outcome, whereas
  // an import let go of from this tab's own dialog was reported inside that dialog. A ref,
  // not state, so the seed effect below cannot add a guid twice if it re-runs before the
  // replace navigation that clears the state has landed.
  const announcedGuids = useRef<Set<string>>(new Set());
  const [deploymentsSearchQuery, setDeploymentsSearchQuery] =
    useState<DeploymentSearchQueryType>({
      project_id: projectId,
      deployment_name: "",
      time_range_start: null,
      time_range_end: null,
      sort_by: "deployment_name",
      sort_direction: "asc",
      skip: 0,
      take: pageSize,
    });

  const location = useLocation();
  const copy = entityCopy("deployments");

  // The import is offered only with the create permission AND real ids for both the
  // organisation and the project: the project page mounts this tab with NaN for both when
  // it could not load its project, and `ImportFromCsv` sends them as `org_id` and
  // `target_project_id` — NaN serialises as null and the API refuses the upload.
  const canImport =
    canAddDeployment && Number.isFinite(orgId) && Number.isFinite(projectId);

  // "Migrate full export" hands off to THIS project's Edit Project page in target mode, so
  // the gate is the deep link's own (AKL-812): all three `MIGRATION_TARGET_PERMISSIONS`, and
  // `is_admin` does not count. `canMigrateFullExport` in csvImport.ts is the organisation
  // gate (CREATE_PROJECT_IN_ORG) and would send a user to a link that opens nothing — when
  // its gate fails the deep link leaves `?migrate=wi` in the URL and the modal shut.
  const offerFullMigration = canOpenWildlifeInsightsMigration({
    existingProject: true,
    isAdmin: !!profile?.is_admin,
    organizationOptionCount: 0,
    perms,
  });

  // The columns this project's deployment template adds to the Akili layout. Always a list
  // here (`[]` = no readable columns, template or not): see the `deploymentSchema` prop for
  // why it is never unknown.
  const extraColumns = useMemo(
    () => schemaColumns(deploymentSchema, uiSchema),
    [deploymentSchema, uiSchema],
  );

  /*
   * Fetch deployments when skip changes.
   */
  useEffect(() => {
    const getDeps = async () => {
      await fetchDeployments();
    };
    getDeps();
  }, [
    deploymentsSearchQuery.skip,
    deploymentsSearchQuery.sort_by,
    deploymentsSearchQuery.sort_direction,
    deploymentsSearchQuery.time_range_end,
  ]);

  /*
   * Load deployments and locations on initial render and when projectId changes or profile loads.
   * Re-runs when the permissions arrive: the project page mounts this tab with `perms=[]`
   * and fills them from a separate request.
   */
  useEffect(() => {
    if (!profileLoading && profile) {
      const isAdmin = profile?.is_admin;
      const hasPermissionToAddDeps = perms.some((perm: any) =>
        perm.name.includes("CREATE_DEPLOYMENTS_LOCATIONS"),
      );
      setCanAddDeployment(isAdmin || hasPermissionToAddDeps);
    }
  }, [profile, profileLoading, perms]);

  // Ordinal of the most recent deployments request. Refreshes can overlap — two import
  // followers settling close together, or a page change while a refresh is in flight — and
  // responses need not come back in order, so only the newest request may write the list;
  // an older response arriving late is dropped.
  const latestDeploymentsRequest = useRef(0);

  /*
   * Fetch deployments from the API. Only the most recently issued request updates the list.
   * Resolves true only when THIS request's response was written to the list; false when the
   * request failed or a newer one superseded it (its response was dropped). Never rejects.
   * A caller that is about to tell the user the list is current reads that answer — the
   * handed-over import toast — everyone else ignores it.
   */
  const fetchDeployments = async (): Promise<boolean> => {
    const requestId = ++latestDeploymentsRequest.current;
    setListLoading(true);
    try {
      const temp = await getDeployments(deploymentsSearchQuery);
      if (requestId !== latestDeploymentsRequest.current) return false;
      setDeployments(temp.deployments || []);
      setTotalCount(temp.count);
      return true;
    } catch (err) {
      if (requestId !== latestDeploymentsRequest.current) return false;
      console.error(err);
      return false;
    } finally {
      // A superseded request must not clear the loading state the newer one still owns.
      if (requestId === latestDeploymentsRequest.current) setListLoading(false);
    }
  };

  /**
   * Automatically open the deployment modal if the location state contains a deploymentId.
   */
  useEffect(() => {
    // if state contains deploymentId, open the modal
    if (
      location.state &&
      location.state.deploymentId &&
      deployments.length > 0
    ) {
      const dep = deployments.find(
        (dep) => dep.deployment_id === location.state.deploymentId,
      );
      if (dep) handleSelectDeployment(dep);
      location.state = {};
    }
  }, [location.state, deployments]);

  /*
   * Follow the imports the Add Deployment page was still following when it sent the user
   * here (AKL-815). Each guid is taken on once — `announcedGuids` remembers it — and the
   * hand-off is then dropped from the history entry, so a refresh or Back does not follow
   * (and toast about) the same imports again.
   */
  useEffect(() => {
    const guids = (location.state as DeploymentsTabState)?.followImportGuids;
    if (!Array.isArray(guids) || guids.length === 0) return;
    const fresh: string[] = [];
    for (const guid of guids) {
      if (typeof guid !== "string" || announcedGuids.current.has(guid)) {
        continue;
      }
      announcedGuids.current.add(guid);
      fresh.push(guid);
    }
    if (fresh.length > 0) {
      setPendingImportGuids((current) => [...current, ...fresh]);
    }
    // Only the state is dropped: the URL keeps whatever query string or hash it carried.
    navigate(
      {
        pathname: location.pathname,
        search: location.search,
        hash: location.hash,
      },
      { replace: true, state: {} },
    );
  }, [location, navigate]);

  /*
   * A followed import has settled (or could not be checked on any more): stop following it,
   * reload the list once and read the job back. `FollowImportUntilSettled` calls the latest
   * copy of this, so the refetch uses the current search query; it does not pass the job
   * through, so the job is read once more here.
   *
   * The read-back serves EVERY followed import, not only the handed-over ones: an applied
   * job that put a deployment template on the project is passed up through
   * `onTemplateApplied`, as `handleImported` does for a job the tab's own dialog saw finish.
   * A job that dialog let go of while still applying is followed from here too, and the
   * page's template props are just as stale when it settles — without the read-back the
   * next import dialog would list no template columns until a reload, with no toast to hint
   * at it. Only the toast is the hand-off's alone.
   *
   * An import handed over by the Add Deployment page is also reported, because the user
   * left before seeing how it ended. The toast follows the job's FINAL status: success for
   * `applied`, the apply-failure sentence for `failed`, and nothing when the follower gave
   * up on a job still in flight (the poll cap) or the read fails — either toast would be a
   * guess. The status read and the refresh run side by side, but the toast waits for both,
   * and says the list is up to date only when that refresh actually wrote it: a refresh
   * that failed, or was superseded by a newer request still out, gets the bare "Import
   * finished." — the longer sentence would be a claim about a list this handler cannot
   * vouch for.
   */
  const handleImportSettled = async (guid: string) => {
    setPendingImportGuids((current) => current.filter((g) => g !== guid));
    const refreshed = fetchDeployments();
    try {
      const job = await getCsvImport(guid);
      if (appliedTemplate(job)) onTemplateApplied?.();
      if (!announcedGuids.current.has(guid)) return;
      const listWritten = await refreshed;
      if (job.status === "applied") {
        toast.success(
          listWritten
            ? t("Import finished. The deployments list is up to date.")
            : t("Import finished."),
        );
      } else if (job.status === "failed") {
        toast.error(t(copy.applyFailure));
      }
    } catch (err) {
      console.error("Error reading a settled CSV import:", err);
    }
  };

  /*
   * Opens the CSV import (AKL-815).
   */
  const openImportModal = () => {
    setImportOpen(true);
  };

  /*
   * An import was applied: reload the list. Called directly rather than through a
   * `skip: 0` update, which does not re-run the load effect when skip is already 0.
   * When the report says the import applied a deployment template to the project, the
   * owner of the project state is told, so `deploymentSchema`/`uiSchema` catch up before
   * the dialog is opened again.
   */
  const handleImported = (job: CsvImportJobDto) => {
    fetchDeployments();
    if (appliedTemplate(job)) onTemplateApplied?.();
  };

  /*
   * The dialog is letting go of a job — on close, or when the user picks another file after
   * polling gave up. A job the server was still applying is followed from here until it
   * settles (see `pendingImportGuids`); nothing else needs following: a dry run writes
   * nothing, and a job already applied refreshed the list through `handleImported`.
   */
  const followIfStillApplying = (job: CsvImportJobDto | null) => {
    if (job?.status === "applying") {
      setPendingImportGuids((current) =>
        current.includes(job.guid) ? current : [...current, job.guid],
      );
    }
  };

  /*
   * The import dialog closed. A closed dialog cannot be reopened to the same job — no web
   * surface renders the project's migration history — so an apply still running is followed
   * from here. A job that had already been applied needs nothing: `handleImported`
   * refreshed the list the moment it reached that state, so refetching again on Done would
   * only repeat the request.
   */
  const handleImportClose = (lastJob: CsvImportJobDto | null) => {
    setImportOpen(false);
    followIfStillApplying(lastJob);
  };

  /*
   * The user chose a full Wildlife Insights migration instead: hand off to this project's
   * Edit Project page, which opens the migration modal in target mode from the
   * `?migrate=wi` deep link (AKL-812).
   */
  const handleMigrateFullExport = () => {
    setImportOpen(false);
    navigate(`/projects/${projectGuid}/overview?migrate=wi`);
  };

  const requestSort = (sortby: string, sortdirection: "asc" | "desc") => {
    setDeploymentsSearchQuery((prevQuery) => ({
      ...prevQuery,
      sort_by: sortby,
      sort_direction: sortdirection,
      skip: 0,
    }));
  };

  /*
   * Handle deployment selection from the list.
   */
  const handleSelectDeployment = (dep: DeploymentResponseDto) => {
    navigate(`/projects/${projectGuid}/deployments/${dep.guid}`);
  };

  const handleSelectAddDeployment = () => {
    navigate(`/projects/${projectGuid}/deployments/add`);
  };

  const handleSearchQueryChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
    >,
  ) => {
    setDeploymentsSearchQuery((prevQuery) => ({
      ...prevQuery,
      [e.target.name]: e.target.value,
    }));

    // reset skip to 0 when date range changes, trigger fetchDeployments
    if (
      e.target.name === "time_range_end" &&
      e.target.value !== deploymentsSearchQuery.time_range_end
    ) {
      setDeploymentsSearchQuery((prevQuery) => ({
        ...prevQuery,
        skip: 0,
      }));
    }
  };

  /*
   * Fetch deployments when search query changes.
   */
  const handleSearch = async () => {
    if (deploymentsSearchQuery.skip !== 0) {
      // reset skip to 0 when searching, which will trigger fetch deployments
      setDeploymentsSearchQuery((prevQuery) => ({
        ...prevQuery,
        skip: 0,
      }));
    } else {
      await fetchDeployments();
    }
  };

  const handlePageClick = (e: any) => {
    setDeploymentsSearchQuery((prevQuery) => ({
      ...prevQuery,
      skip: e.selected * pageSize,
    }));
  };

  return (
    <>
      {/* Never mounted without CREATE_DEPLOYMENTS_LOCATIONS: the API's 403 on the upload
          would be a dead end for the user (no longer a sign-out since AKL-939, but still
          a bundle chosen for nothing). Both formats — a Wildlife
          Insights deployments.csv usually comes out of a full export, so the fork is
          offered to a user the target-mode deep link would let through. */}
      {canImport && importOpen && (
        <div className="fixed z-50">
          <ImportFromCsv
            isOpen={importOpen}
            orgId={orgId}
            entity="deployments"
            formats={["akili_csv", "ctms_csv"]}
            targetProjectId={projectId}
            extraColumns={extraColumns}
            canMigrateFullExport={offerFullMigration}
            onClose={handleImportClose}
            onImported={handleImported}
            onMigrateFullExport={handleMigrateFullExport}
            onJobAbandoned={followIfStillApplying}
          />
        </div>
      )}
      {/* One follower per import still applying after its dialog let go of it (or handed
          over by the Add page); each has its own poller, so a second one never cancels the
          first. Render nothing visible. */}
      {pendingImportGuids.map((guid) => (
        <FollowImportUntilSettled
          key={guid}
          guid={guid}
          onSettled={handleImportSettled}
        />
      ))}
      {/* mx-8 keeps tab content aligned with the tab strip above it. */}
      <div className="mx-8 mt-6 mb-4">
        {/* SearchBarDeployments + DeploymentsList render card-interior
            content — they expect this tableCard wrapper (filter toolbar
            attached to the table), matching the migrated
            OrganizationProjectsTab pattern. */}
        <div className={tailwindClassnames.tableCard}>
          <SearchBarDeployments
            canAddDeployment={canAddDeployment}
            onClickAddDeployment={handleSelectAddDeployment}
            onChangeSearchQuery={handleSearchQueryChange}
            onSearch={handleSearch}
            tempDeploymentsSearchQuery={deploymentsSearchQuery}
            onImportFromCsv={canImport ? openImportModal : undefined}
          />

          <DeploymentsList
            listLoading={listLoading}
            deployments={deployments}
            requestSort={requestSort}
            onSelectDeployment={handleSelectDeployment}
            onPageClicked={handlePageClick}
            take={deploymentsSearchQuery.take ?? pageSize}
            offset={deploymentsSearchQuery.skip ?? 0}
            totalCount={totalCount}
          />
        </div>
      </div>
    </>
  );
};

export default ManageProjectDeployments;
